import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  Typography,
} from '@mui/material';
import {
  DATE_FORMAT,
  SnackbarApiErrorMessage,
  useDataProvider,
  useLocalization,
  useSnackbar,
} from '@ui-framework/index';
import { FormattedMessage } from 'react-intl';
import { DialogCloseButton } from '@product-framework/components/Dialogs';
import {
  AvailabilitySlotTypeProvider,
  IconLibrary,
  useAvailabilitySlotTypes,
} from '@product-framework/index';
import dayjs from 'dayjs';

function DispatchJobContent({ success, loading, value }) {
  const { getSlotByCode } = useAvailabilitySlotTypes();
  return (
    <Box>
      {!success && (
        <Box>
          {loading ? (
            <Box textAlign="center">
              <CircularProgress size="30px" />
              <Typography>
                <FormattedMessage
                  id="JOB_DISPATCH_IN_PROGRESS"
                  defaultMessage="Job dispatch in progress"
                />
                ...
              </Typography>
            </Box>
          ) : (
            <Typography variant="body">
              Do you want to dispatch the job for{' '}
              <Typography component="span" sx={{ fontWeight: 500 }}>
                {dayjs(value?.textDate).format(
                  DATE_FORMAT.FRIENDLY_WEEKDAY_DATE,
                )}
                &ndash;
                <FormattedMessage
                  id={value?.slot?.code || ''}
                  defaultMessage={
                    getSlotByCode({ code: value?.slot?.code })?.name
                  }
                />
              </Typography>{' '}
              time slot?
            </Typography>
          )}
        </Box>
      )}
      {success && (
        <Box sx={{ mt: 2 }} textAlign="center">
          <Typography>
            <FormattedMessage
              id="JOB_DISPATCHED_SUCCESSFULLY"
              defaultMessage="Job Dispatched Successfully"
            />
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function DispatchJobDialog({
  open,
  returnToJobPage,
  onClose,
  value,
  jobInfo,
  setErrors,
}) {
  const { formatMessage } = useLocalization();
  const snackbar = useSnackbar();

  const jobId = jobInfo?.id;
  const jobNumber = jobInfo?.serviceJobNumber;

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { post, getApiUrl } = useDataProvider(provider);

  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const dispatchJob = async () => {
    setLoading(true);
    const url = `${getApiUrl()}/sxcl/v1/service-jobs/${jobId}/operations/dispatch`;
    const response = await post(url, {
      date: value?.textDate,
      slot: {
        code: value?.slot?.code,
      },
      recordVersion: jobInfo?.recordVersion,
    });
    if (response?.ok) {
      setSuccess(true);
      setLoading(false);
    } else {
      setErrors(response?.errors || []);
      snackbar.showMessage({
        type: 'error',
        data: <SnackbarApiErrorMessage errors={response?.errors} />,
      });
      setLoading(false);
      onClose();
    }
  };

  const buttons = [
    ...(!success
      ? [
          {
            label: formatMessage({
              id: 'DISPATCH',
              defaultMessage: 'Dispatch',
            }),
            onClick: () => dispatchJob(),
            variant: 'contained',
            color: 'primary',
            startIcon: <IconLibrary.Dispatch />,
          },
          {
            label: formatMessage({
              id: 'CANCEL',
              defaultMessage: 'Cancel',
            }),
            onClick: () => onClose(),
            variant: 'outlined',
            color: 'primary',
            startIcon: <IconLibrary.Cancel />,
          },
        ]
      : [
          {
            label: formatMessage({
              id: 'CLOSE',
              defaultMessage: 'Close',
            }),
            onClick: () => returnToJobPage(),
            variant: 'contained',
            color: 'primary',
            startIcon: <IconLibrary.Close />,
          },
        ]),
  ];

  return (
    <Dialog
      open={open}
      onClose={() => {
        onClose(null);
      }}
      maxWidth="sm"
      fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconLibrary.Dispatch />
        <FormattedMessage id="DISPATCH_JOB" defaultMessage="Dispatch Job" />
        &nbsp;&ndash;
        <Typography color="primary" variant="body">
          {jobNumber}
        </Typography>
        <DialogCloseButton
          onClose={() => {
            onClose(null);
          }}
        />
      </DialogTitle>
      <DialogContent>
        <AvailabilitySlotTypeProvider>
          <DispatchJobContent
            success={success}
            loading={loading}
            value={value}
          />
        </AvailabilitySlotTypeProvider>
      </DialogContent>

      <DialogActions display="flex" sx={{ justifyContent: 'center', my: 2 }}>
        {buttons.map(btn => {
          const { label, ...rest } = btn;
          return (
            <Button key={label} {...rest}>
              {label}
            </Button>
          );
        })}
      </DialogActions>
    </Dialog>
  );
}

export default DispatchJobDialog;
